import { useId } from "react";

export const CLARITEV_CHART_THEME = Object.freeze({
  colors: {
    cyan: "#00B5D6",
    azure: "#058DF0",
    blue: "#2F53A4",
    navy: "#001A47",
    lime: "#C8D322",
    magenta: "#E70057",
    text: "#001946",
    inverseText: "#FFFFFF",
    surface: "#FFFFFF",
    subtleSurface: "#F8F8F8",
    darkCategory4: "#F8F8F8",
    gridline: "#D9D9D9",
  },
  categorical: ["#00B5D6", "#058DF0", "#2F53A4", "#001A47", "#C8D322", "#E70057"],
  trendSeries: [
    { color: "#00B5D6", dash: undefined, marker: "circle" },
    { color: "#E70057", dash: "6 4", marker: "diamond" },
    { color: "#2F53A4", dash: "2 3", marker: "square" },
    { color: "#C8D322", dash: "8 3", marker: "diamond" },
  ],
});

function buildPath(points) {
  return points.map((point, index) => `${index === 0 ? "M" : "L"}${point[0]} ${point[1]}`).join(" ");
}

function pathGroups(values, xFor, yFor) {
  const groups = [];
  let current = [];
  values.forEach((value, index) => {
    if (value == null || !Number.isFinite(Number(value))) {
      if (current.length) groups.push(current);
      current = [];
    } else {
      current.push([xFor(index), yFor(Number(value))]);
    }
  });
  if (current.length) groups.push(current);
  return groups;
}

function visibleTick(index, total, maximum = 6) {
  if (index === 0 || index === total - 1) return true;
  const step = Math.max(1, Math.ceil((total - 1) / Math.max(maximum - 1, 1)));
  return index % step === 0 && index < total - 1;
}

function ChartDataTable({ title, headers, rows, mode = "disclosure" }) {
  if (!rows?.length) return null;
  const table = (
    <table className="chart-data-table">
      <caption className="sr-only">{title} exact values</caption>
      <thead>
        <tr>
          {headers.map((header, index) => (
            <th key={header} scope="col" className={index > 0 ? "numeric" : undefined}>
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={`${row[0]}-${rowIndex}`}>
            {row.map((value, cellIndex) =>
              cellIndex === 0 ? (
                <th key={cellIndex} scope="row">
                  {value}
                </th>
              ) : (
                <td key={cellIndex} className="numeric">
                  {value}
                </td>
              )
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
  if (mode === "sr-only") return <div className="sr-only">{table}</div>;
  return (
    <details className="chart-data-disclosure">
      <summary>View data table</summary>
      <div className="chart-data-scroll" tabIndex={0} role="region" aria-label={`${title} data table`}>
        {table}
      </div>
    </details>
  );
}

function ChartFigure({ title, summary, headers, rows, tableMode, className = "", children }) {
  const titleId = useId();
  const summaryId = useId();
  return (
    <figure
      className={`claritev-chart ${className}`.trim()}
      aria-labelledby={titleId}
      aria-describedby={summary ? summaryId : undefined}
    >
      <figcaption id={titleId} className="sr-only">
        {title}
      </figcaption>
      {summary && (
        <p id={summaryId} className="sr-only">
          {summary}
        </p>
      )}
      {children}
      <ChartDataTable title={title} headers={headers} rows={rows} mode={tableMode} />
    </figure>
  );
}

function EmptyChart({ title, message = "No data available" }) {
  return (
    <ChartFigure title={title} summary={message}>
      <div className="chart-empty claritev-chart-empty" role="status">
        {message}
      </div>
    </ChartFigure>
  );
}

function PointMarker({ marker = "circle", x, y, color, label, tooltipLabel = label, size = 6 }) {
  const common = {
    fill: color,
    stroke: CLARITEV_CHART_THEME.colors.surface,
    strokeWidth: 1.5,
    tabIndex: 0,
    role: "img",
    "aria-label": label,
  };
  if (marker === "diamond") {
    return (
      <polygon {...common} points={`${x},${y - size} ${x + size},${y} ${x},${y + size} ${x - size},${y}`}>
        <title>{tooltipLabel}</title>
      </polygon>
    );
  }
  if (marker === "square") {
    return (
      <rect {...common} x={x - size} y={y - size} width={size * 2} height={size * 2}>
        <title>{tooltipLabel}</title>
      </rect>
    );
  }
  return (
    <circle {...common} cx={x} cy={y} r={size}>
      <title>{tooltipLabel}</title>
    </circle>
  );
}

function TrendLegend({ series, markerLabel, showMarker }) {
  return (
    <div className="monitoring-chart-legend" aria-label="Chart legend">
      {series.map((item) => (
        <span key={item.key}>
          <i
            className={`chart-series-key marker-${item.marker || "circle"}`}
            style={{
              "--series-color": item.color,
              "--series-dash": item.dash
                ? "repeating-linear-gradient(90deg, var(--series-color) 0 7px, transparent 7px 11px)"
                : "var(--series-color)",
            }}
          />
          {item.label}
        </span>
      ))}
      {showMarker && (
        <span>
          <i className="campaign-marker-key" />
          {markerLabel}
        </span>
      )}
    </div>
  );
}

function lineSummary(labels, series, format) {
  const latestIndex = labels.length - 1;
  const latest = series
    .map((item) => {
      const value = item.values[latestIndex];
      return `${item.label}: ${value == null ? "No data" : format(value)}`;
    })
    .join("; ");
  const hasMissing = series.some((item) => item.values.some((value) => value == null));
  return `Latest period ${labels[latestIndex]}. ${latest}.${hasMissing ? " Missing periods are not connected." : ""}`;
}

function yDomain(values, zeroBaseline = true) {
  const visible = values.filter((value) => value != null && Number.isFinite(Number(value))).map(Number);
  const rawMax = visible.length ? Math.max(...visible) : 0;
  const rawMin = visible.length ? Math.min(...visible) : 0;
  if (zeroBaseline) return { min: 0, max: Math.max(rawMax, 0) || 1 };
  const range = rawMax - rawMin || Math.max(Math.abs(rawMax) * 0.1, 1);
  return { min: rawMin - range * 0.08, max: rawMax + range * 0.08 };
}

export function LineChart({
  labels,
  values,
  title = "Metric by period",
  width = 760,
  height = 320,
  color = CLARITEV_CHART_THEME.colors.cyan,
  format = (value) => value,
  zeroBaseline = true,
}) {
  if (!values?.length || !labels?.length) return <EmptyChart title={title} />;
  const plotHeight = Math.max(height, 240);
  const padding = { top: 24, right: 24, bottom: 42, left: 64 };
  const innerW = width - padding.left - padding.right;
  const innerH = plotHeight - padding.top - padding.bottom;
  const { min, max } = yDomain(values, zeroBaseline);
  const span = max - min || 1;
  const xFor = (index) =>
    values.length === 1 ? padding.left + innerW / 2 : padding.left + (index / (values.length - 1)) * innerW;
  const yFor = (value) => padding.top + innerH - ((value - min) / span) * innerH;
  const latestValue = values.at(-1);
  const rows = labels.map((label, index) => [
    label,
    values[index] == null ? "No data" : format(values[index]),
  ]);
  return (
    <ChartFigure
      title={title}
      summary={`Latest period ${labels.at(-1)}: ${latestValue == null ? "No data" : format(latestValue)}.`}
      headers={["Period", "Value"]}
      rows={rows}
      className="chart-line"
    >
      <svg viewBox={`0 0 ${width} ${plotHeight}`} className="chart-wrap" role="img">
        <title>{title}</title>
        <desc>{`Latest period ${labels.at(-1)}: ${latestValue == null ? "No data" : format(latestValue)}.`}</desc>
        {Array.from({ length: 5 }, (_, index) => {
          const value = min + (span * index) / 4;
          const y = yFor(value);
          return (
            <g key={index}>
              <line x1={padding.left} x2={padding.left + innerW} y1={y} y2={y} className="grid-line" />
              <text x={padding.left - 10} y={y + 5} textAnchor="end" className="tick-label">
                {format(Math.round(value))}
              </text>
            </g>
          );
        })}
        {pathGroups(values, xFor, yFor).map((points, index) => (
          <path key={index} d={buildPath(points)} fill="none" stroke={color} strokeWidth="3" />
        ))}
        {values.map((value, index) =>
          value == null ? null : (
            <PointMarker
              key={labels[index]}
              x={xFor(index)}
              y={yFor(Number(value))}
              color={color}
              label={`${labels[index]}: ${format(value)}`}
              tooltipLabel={format(value)}
            />
          )
        )}
        {labels.map((label, index) =>
          visibleTick(index, labels.length) ? (
            <text key={label} x={xFor(index)} y={plotHeight - 12} textAnchor="middle" className="tick-label">
              {label}
            </text>
          ) : null
        )}
      </svg>
      <TrendLegend series={[{ key: "value", label: title, color, marker: "circle" }]} showMarker={false} />
    </ChartFigure>
  );
}

export function DualLineChart({
  labels,
  seriesA,
  seriesB,
  labelA,
  labelB,
  title = "Metric comparison by period",
  width = 760,
  height = 320,
  format = (value) => value,
}) {
  const series = [
    { key: "a", label: labelA, values: seriesA || [], ...CLARITEV_CHART_THEME.trendSeries[0] },
    { key: "b", label: labelB, values: seriesB || [], ...CLARITEV_CHART_THEME.trendSeries[1] },
  ];
  return (
    <MultiLineChart
      labels={labels}
      series={series}
      title={title}
      width={width}
      height={height}
      format={format}
    />
  );
}

export function MultiLineChart({
  labels,
  series,
  title = "Metrics by period",
  markers = [],
  markerLabel = "Campaign publication",
  width = 760,
  height = 320,
  format = (value) => value,
  tickFormat = (value) => format(Math.round(value)),
  zeroBaseline = true,
  showDataTable = true,
}) {
  const normalizedSeries = (series || []).slice(0, 4).map((item, index) => ({
    ...CLARITEV_CHART_THEME.trendSeries[index],
    ...item,
  }));
  const visibleValues = normalizedSeries.flatMap((item) => item.values.filter((value) => value != null));
  if (!labels?.length || !visibleValues.length)
    return <EmptyChart title={title} message="No trend data available" />;
  const plotHeight = Math.max(height, 240);
  const padding = { top: 24, right: 24, bottom: 42, left: 64 };
  const innerW = width - padding.left - padding.right;
  const innerH = plotHeight - padding.top - padding.bottom;
  const { min, max: rawMax } = yDomain(visibleValues, zeroBaseline);
  const max = rawMax + (rawMax - min || 1) * 0.06;
  const span = max - min || 1;
  const xFor = (index) =>
    labels.length === 1 ? padding.left + innerW / 2 : padding.left + (index / (labels.length - 1)) * innerW;
  const yFor = (value) => padding.top + innerH - ((value - min) / span) * innerH;
  const visibleMarkers = markers.filter((marker) => labels.includes(marker.month));
  const rows = labels.map((label, index) => [
    label,
    ...normalizedSeries.map((item) => (item.values[index] == null ? "No data" : format(item.values[index]))),
  ]);

  return (
    <ChartFigure
      title={title}
      summary={lineSummary(labels, normalizedSeries, format)}
      headers={["Period", ...normalizedSeries.map((item) => item.label)]}
      rows={showDataTable ? rows : undefined}
      className="chart-line"
    >
      <svg viewBox={`0 0 ${width} ${plotHeight}`} className="chart-wrap" role="img">
        <title>{title}</title>
        <desc>{lineSummary(labels, normalizedSeries, format)}</desc>
        {Array.from({ length: 5 }, (_, index) => {
          const value = min + (span * index) / 4;
          const y = yFor(value);
          return (
            <g key={index}>
              <line x1={padding.left} x2={padding.left + innerW} y1={y} y2={y} className="grid-line" />
              <text x={padding.left - 10} y={y + 5} textAnchor="end" className="tick-label">
                {tickFormat(value)}
              </text>
            </g>
          );
        })}
        {visibleMarkers.map((marker) => {
          const x = xFor(labels.indexOf(marker.month));
          return (
            <g key={marker.key || `${marker.month}-${marker.label}`}>
              <line
                x1={x}
                x2={x}
                y1={padding.top}
                y2={padding.top + innerH}
                className="campaign-marker-line"
              />
              <circle cx={x} cy={padding.top + 7} r="6" className="campaign-marker-dot" tabIndex="0">
                <title>{marker.label}</title>
              </circle>
              {marker.count > 1 && (
                <text x={x + 9} y={padding.top + 11} className="campaign-marker-count">
                  {marker.count}
                </text>
              )}
            </g>
          );
        })}
        {normalizedSeries.map((item) => (
          <g key={item.key}>
            {pathGroups(item.values, xFor, yFor).map((points, index) => (
              <path
                key={index}
                d={buildPath(points)}
                fill="none"
                stroke={item.color}
                strokeWidth="3"
                strokeDasharray={item.dash}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ))}
            {item.values.map((value, index) =>
              value == null ? null : (
                <PointMarker
                  key={`${item.key}-${labels[index]}`}
                  marker={item.marker}
                  x={xFor(index)}
                  y={yFor(value)}
                  color={item.color}
                  size={4.5}
                  label={`${item.label}, ${labels[index]}: ${format(value)}`}
                  tooltipLabel={`${item.label}: ${format(value)}`}
                />
              )
            )}
          </g>
        ))}
        {labels.map((label, index) =>
          visibleTick(index, labels.length) ? (
            <text key={label} x={xFor(index)} y={plotHeight - 12} textAnchor="middle" className="tick-label">
              {label}
            </text>
          ) : null
        )}
      </svg>
      <TrendLegend
        series={normalizedSeries}
        markerLabel={markerLabel}
        showMarker={visibleMarkers.length > 0}
      />
    </ChartFigure>
  );
}

export function StackedBarChart({
  labels,
  series,
  title = "Population composition by period",
  width = 760,
  height = 320,
  format = (value) => value,
  showDataTable = true,
}) {
  const normalizedSeries = (series || []).map((item, index) => ({
    color: CLARITEV_CHART_THEME.categorical[index % CLARITEV_CHART_THEME.categorical.length],
    ...item,
  }));
  const hasData = normalizedSeries.some((item) => item.values.some((value) => value != null));
  if (!labels?.length || !hasData) return <EmptyChart title={title} message="No trend data available" />;

  const plotHeight = Math.max(height, 240);
  const padding = { top: 24, right: 24, bottom: 42, left: 64 };
  const innerW = width - padding.left - padding.right;
  const innerH = plotHeight - padding.top - padding.bottom;
  const stackTotals = labels.map((_, labelIndex) => {
    const values = normalizedSeries.map((item) => item.values[labelIndex]);
    return values.every((value) => value != null)
      ? values.reduce((total, value) => total + Number(value), 0)
      : 0;
  });
  const rawMax = Math.max(...stackTotals, 0) || 1;
  const roughStep = rawMax / 4;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep || 1));
  const step = Math.ceil(roughStep / magnitude) * magnitude;
  const max = step * 4;
  const slotWidth = innerW / labels.length;
  const barWidth = Math.min(slotWidth * 0.68, 42);
  const rows = labels.map((label, index) => [
    label,
    ...normalizedSeries.map((item) => (item.values[index] == null ? "No data" : format(item.values[index]))),
  ]);

  return (
    <ChartFigure
      title={title}
      headers={["Period", ...normalizedSeries.map((item) => item.label)]}
      rows={showDataTable ? rows : undefined}
      className="chart-bars"
    >
      <svg viewBox={`0 0 ${width} ${plotHeight}`} className="chart-wrap" role="img">
        <title>{title}</title>
        {Array.from({ length: 5 }, (_, index) => {
          const value = step * index;
          const y = padding.top + innerH - (value / max) * innerH;
          return (
            <g key={index}>
              <line x1={padding.left} x2={padding.left + innerW} y1={y} y2={y} className="grid-line" />
              <text x={padding.left - 10} y={y + 5} textAnchor="end" className="tick-label">
                {format(value)}
              </text>
            </g>
          );
        })}
        {labels.map((label, labelIndex) => {
          const x = padding.left + labelIndex * slotWidth + (slotWidth - barWidth) / 2;
          const values = normalizedSeries.map((item) => item.values[labelIndex]);
          const complete = values.every((value) => value != null);
          let cumulative = 0;
          return (
            <g key={label}>
              {complete &&
                normalizedSeries.map((item, seriesIndex) => {
                  const value = Number(values[seriesIndex]);
                  const segmentHeight = (value / max) * innerH;
                  const y = padding.top + innerH - ((cumulative + value) / max) * innerH;
                  cumulative += value;
                  return (
                    <rect
                      key={item.key}
                      x={x}
                      y={y}
                      width={barWidth}
                      height={segmentHeight}
                      fill={item.color}
                      tabIndex="0"
                    >
                      <title>{`${item.label}, ${label}: ${format(value)}`}</title>
                    </rect>
                  );
                })}
              {visibleTick(labelIndex, labels.length) && (
                <text x={x + barWidth / 2} y={plotHeight - 12} textAnchor="middle" className="tick-label">
                  {label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div className="monitoring-chart-legend" aria-label="Chart legend">
        {normalizedSeries.map((item) => (
          <span key={item.key} className={item.target ? "target-series-label" : undefined}>
            <i className="stacked-bar-key" style={{ backgroundColor: item.color }} />
            {item.label}
          </span>
        ))}
      </div>
    </ChartFigure>
  );
}

export function HBarChart({
  data,
  title = "Members by category",
  width = 760,
  height = null,
  color = CLARITEV_CHART_THEME.colors.blue,
  format = (value) => value,
}) {
  if (!data?.length) return <EmptyChart title={title} />;
  const rowHeight = 34;
  const gap = 12;
  const plotHeight = Math.max(height ?? data.length * (rowHeight + gap) + 54, 240);
  const max = Math.max(...data.map((item) => item.v ?? 0), 0) || 1;
  const padLeft = 230;
  const padRight = 90;
  const innerW = width - padLeft - padRight;
  const rows = data.map((item) => [item.name, item.v == null ? "Suppressed (small cohort)" : format(item.v)]);
  return (
    <ChartFigure
      title={title}
      summary={`${data.length} categories. Largest visible value is ${format(max)}.`}
      headers={["Category", "Members"]}
      rows={rows}
      className="chart-bars"
    >
      <svg viewBox={`0 0 ${width} ${plotHeight}`} className="chart-wrap" role="img">
        <title>{title}</title>
        <line x1={padLeft} x2={padLeft} y1={20} y2={plotHeight - 24} className="chart-axis-line" />
        {data.map((item, index) => {
          const y = 24 + index * (rowHeight + gap);
          const suppressed = item.v == null;
          const barWidth = suppressed ? 0 : (item.v / max) * innerW;
          return (
            <g key={item.name}>
              <text
                x={padLeft - 12}
                y={y + rowHeight / 2 + 5}
                className="tick-label bar-label"
                textAnchor="end"
              >
                {item.name}
              </text>
              {suppressed ? (
                <text x={padLeft + 10} y={y + rowHeight / 2 + 5} className="tick-label suppressed-label">
                  Suppressed (small cohort)
                </text>
              ) : (
                <>
                  <rect
                    x={padLeft}
                    y={y}
                    width={barWidth}
                    height={rowHeight}
                    fill={item.color || color}
                    tabIndex="0"
                  >
                    <title>{`${item.name}: ${format(item.v)}`}</title>
                  </rect>
                  <text x={padLeft + barWidth + 10} y={y + rowHeight / 2 + 5} className="data-label">
                    {format(item.v)}
                  </text>
                </>
              )}
            </g>
          );
        })}
      </svg>
    </ChartFigure>
  );
}

export function VBarChart({
  data,
  title = "Members by category",
  width = 760,
  height = 320,
  yLabel = "",
  color = CLARITEV_CHART_THEME.colors.blue,
  format = (value) => value,
}) {
  if (!data?.length) return <EmptyChart title={title} />;
  const plotHeight = Math.max(height, 240);
  const padding = { top: 36, right: 24, bottom: 52, left: 68 };
  const innerW = width - padding.left - padding.right;
  const innerH = plotHeight - padding.top - padding.bottom;
  const maxValue = Math.max(...data.map((item) => item.v ?? 0), 0);
  const max = Math.ceil(maxValue / 10) * 10 || 10;
  const slotWidth = innerW / data.length;
  const barWidth = Math.min(slotWidth * 0.57, 72);
  const rows = data.map((item) => [item.name, item.v == null ? "Suppressed (small cohort)" : format(item.v)]);
  return (
    <ChartFigure
      title={title}
      summary={`${data.length} categories. Largest visible value is ${format(maxValue)}.`}
      headers={["Category", yLabel || "Value"]}
      rows={rows}
      className="chart-bars"
    >
      <svg viewBox={`0 0 ${width} ${plotHeight}`} className="chart-wrap" role="img">
        <title>{title}</title>
        {Array.from({ length: 5 }, (_, index) => {
          const value = (max * index) / 4;
          const y = padding.top + innerH - (value / max) * innerH;
          return (
            <g key={index}>
              <line x1={padding.left} x2={padding.left + innerW} y1={y} y2={y} className="grid-line" />
              <text x={padding.left - 10} y={y + 5} textAnchor="end" className="tick-label">
                {format(Math.round(value))}
              </text>
            </g>
          );
        })}
        {yLabel && (
          <text
            x={16}
            y={padding.top + innerH / 2}
            textAnchor="middle"
            className="axis-title"
            transform={`rotate(-90, 16, ${padding.top + innerH / 2})`}
          >
            {yLabel}
          </text>
        )}
        {data.map((item, index) => {
          const x = padding.left + index * slotWidth + (slotWidth - barWidth) / 2;
          const suppressed = item.v == null;
          const barHeight = suppressed ? 0 : (item.v / max) * innerH;
          const y = padding.top + innerH - barHeight;
          return (
            <g key={item.name}>
              {!suppressed && (
                <rect x={x} y={y} width={barWidth} height={barHeight} fill={item.color || color} tabIndex="0">
                  <title>{`${item.name}: ${format(item.v)}`}</title>
                </rect>
              )}
              <text
                x={x + barWidth / 2}
                y={suppressed ? padding.top + innerH - 8 : y - 9}
                textAnchor="middle"
                className={suppressed ? "suppressed-label" : "data-label"}
              >
                {suppressed ? "< min" : format(item.v)}
              </text>
              <text x={x + barWidth / 2} y={plotHeight - 18} textAnchor="middle" className="tick-label">
                {item.name}
              </text>
            </g>
          );
        })}
      </svg>
    </ChartFigure>
  );
}

export function Sparkline({
  values,
  width = 120,
  height = 28,
  color = CLARITEV_CHART_THEME.colors.cyan,
  label = "Trend",
  format = (value) => value,
}) {
  if (!values?.length) return <span className="sparkline-empty">No data</span>;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const points = values.map((value, index) => [
    (index / Math.max(values.length - 1, 1)) * (width - 8) + 4,
    height - 4 - ((value - min) / (max - min || 1)) * (height - 10),
  ]);
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="sparkline"
      style={{ width, height }}
      role="img"
      aria-label={`${label}: ${format(values[0])} to ${format(values.at(-1))}`}
    >
      <path d={buildPath(points)} fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" />
      <circle
        cx={points.at(-1)[0]}
        cy={points.at(-1)[1]}
        r="4"
        fill={color}
        stroke="#fff"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function polarPoint(cx, cy, radius, angleDegrees) {
  const radians = ((angleDegrees - 90) * Math.PI) / 180;
  return { x: cx + radius * Math.cos(radians), y: cy + radius * Math.sin(radians) };
}

function doughnutPath(cx, cy, outerRadius, innerRadius, startAngle, endAngle) {
  const safeEndAngle = endAngle - startAngle >= 359.999 ? startAngle + 359.999 : endAngle;
  const startOuter = polarPoint(cx, cy, outerRadius, safeEndAngle);
  const endOuter = polarPoint(cx, cy, outerRadius, startAngle);
  const startInner = polarPoint(cx, cy, innerRadius, startAngle);
  const endInner = polarPoint(cx, cy, innerRadius, safeEndAngle);
  const largeArc = safeEndAngle - startAngle > 180 ? 1 : 0;
  return [
    `M ${startOuter.x} ${startOuter.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArc} 0 ${endOuter.x} ${endOuter.y}`,
    `L ${startInner.x} ${startInner.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArc} 1 ${endInner.x} ${endInner.y}`,
    "Z",
  ].join(" ");
}

export function DoughnutChart({
  segments,
  title,
  centerValue,
  centerLabel,
  size = 128,
  format = (value) => value,
}) {
  const safeSegments = (segments || []).filter((segment) => Number(segment.value) >= 0).slice(0, 6);
  const total = safeSegments.reduce((sum, segment) => sum + Number(segment.value), 0);
  if (!safeSegments.length || total <= 0) {
    return (
      <ChartFigure title={title} summary="No enrollment data available" className="chart-doughnut">
        <div className="doughnut-empty" role="status" style={{ width: size, height: size }}>
          No data
        </div>
      </ChartFigure>
    );
  }
  let angle = 0;
  const rows = safeSegments.map((segment) => [
    segment.label,
    format(segment.value),
    `${Math.round((segment.value / total) * 100)}%`,
  ]);
  return (
    <ChartFigure
      title={title}
      summary={`${centerValue} ${centerLabel}. ${safeSegments.map((segment) => `${segment.label}: ${format(segment.value)}`).join("; ")}.`}
      headers={["Category", "Members", "Share"]}
      rows={rows}
      tableMode="sr-only"
      className="chart-doughnut"
    >
      <div className="doughnut-plot" style={{ width: size, height: size }}>
        <svg viewBox="0 0 120 120" className="chart-wrap" role="img">
          <title>{title}</title>
          {safeSegments.map((segment, index) => {
            const start = angle;
            const end = angle + (segment.value / total) * 360;
            angle = end;
            if (segment.value <= 0) return null;
            const color = segment.color || CLARITEV_CHART_THEME.categorical[index];
            return (
              <path
                key={segment.id || segment.label}
                d={doughnutPath(60, 60, 56, 35, start, end)}
                fill={color}
                stroke={CLARITEV_CHART_THEME.colors.surface}
                strokeWidth="2"
                tabIndex="0"
                role="img"
                aria-label={`${segment.label}: ${format(segment.value)}, ${Math.round((segment.value / total) * 100)}%`}
              >
                <title>{`${segment.label}: ${format(segment.value)} (${Math.round((segment.value / total) * 100)}%)`}</title>
              </path>
            );
          })}
        </svg>
        <div className="doughnut-center" aria-hidden="true">
          <strong>{centerValue}</strong>
          <span>{centerLabel}</span>
        </div>
      </div>
    </ChartFigure>
  );
}

export function StageFunnel({ stages, minCohortSize = 25, title = "Members by clinical stage" }) {
  if (!stages?.length) return <EmptyChart title={title} />;
  const max = Math.max(...stages.map((stage) => stage.memberCount ?? 0)) || 1;
  const rows = stages.map((stage) => [
    `${stage.stageId}. ${stage.stageName}`,
    stage.suppressed ? `< ${minCohortSize} (suppressed)` : stage.memberCount,
  ]);
  return (
    <ChartFigure
      title={title}
      summary={`${stages.length} clinical stages. Bars compare distinct member counts from a zero baseline.`}
      headers={["Clinical stage", "Members"]}
      rows={rows}
      className="chart-funnel"
    >
      <div className="funnel-plot" role="img" aria-label={title}>
        {stages.map((stage) => (
          <div key={stage.stageId} className={`funnel-row tier-${stage.tier}`}>
            <span className="name">
              {stage.stageId}. {stage.stageName}
            </span>
            {stage.suppressed ? (
              <span className="suppressed-chip">&lt; {minCohortSize} members — suppressed</span>
            ) : (
              <div
                className="bar"
                style={{ width: `${((stage.memberCount ?? 0) / max) * 100}%` }}
                title={`${stage.stageName}: ${stage.memberCount}`}
              />
            )}
            <span className="count">{stage.suppressed ? "" : stage.memberCount}</span>
          </div>
        ))}
      </div>
    </ChartFigure>
  );
}
